import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface PublicNavBarProps {
  showScrollLinks?: boolean;
}

const PublicNavBar: React.FC<PublicNavBarProps> = ({ showScrollLinks = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const isHome = location.pathname === '/';

  const scrollOrNavigate = (section: string) => {
    if (isHome) {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`/#${section}`);
    }
  };

  const handleDashboard = () => {
    if (user?.role === 'customer') navigate('/customer/inquiries');
    else if (user?.role === 'contractor') navigate('/contractor/browse-jobs');
    else navigate('/admin/dashboard');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #ececec',
        color: '#333',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minHeight: { xs: 72, md: 96 } }}>
        <Box
          sx={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          onClick={() => (isHome ? window.scrollTo({ top: 0, behavior: 'smooth' }) : navigate('/'))}
        >
          <img
            src="/callcenter-suche-logo.png"
            alt="Callcenter-Suche"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
          <Button
            size="small"
            sx={{ color: '#555', fontWeight: 500, '&:hover': { color: '#E85D26', backgroundColor: 'rgba(232,93,38,0.06)' } }}
            onClick={() => scrollOrNavigate('features')}
          >
            Vorteile
          </Button>
          <Button
            size="small"
            sx={{ color: '#555', fontWeight: 500, '&:hover': { color: '#E85D26', backgroundColor: 'rgba(232,93,38,0.06)' } }}
            onClick={() => scrollOrNavigate('how-it-works')}
          >
            So funktioniert's
          </Button>
          <Button
            size="small"
            sx={{ color: '#555', fontWeight: 500, '&:hover': { color: '#E85D26', backgroundColor: 'rgba(232,93,38,0.06)' } }}
            onClick={() => scrollOrNavigate('testimonials')}
          >
            Referenzen
          </Button>
          <Button
            size="small"
            sx={{ color: '#555', fontWeight: 500, '&:hover': { color: '#E85D26', backgroundColor: 'rgba(232,93,38,0.06)' } }}
            onClick={() => navigate('/submit-inquiry')}
          >
            Gesuch aufgeben
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
          {isAuthenticated ? (
            <>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                {user?.name}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleDashboard}
                endIcon={<ArrowForwardIcon />}
                sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)' } }}
              >
                Dashboard
              </Button>
              <Button
                size="small"
                sx={{ color: '#555' }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                ▾
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem onClick={() => { navigate('/profile'); setAnchorEl(null); }}>
                  Mein Profil
                </MenuItem>
                <MenuItem onClick={async () => { await logout(); navigate('/'); setAnchorEl(null); }}>
                  Abmelden
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ borderColor: '#E85D26', color: '#E85D26', '&:hover': { borderColor: '#c94d1e', backgroundColor: 'rgba(232,93,38,0.06)' } }}
              >
                Anmelden
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/register')}
                sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700, '&:hover': { background: 'linear-gradient(135deg, #d94f1e 0%, #b8431a 100%)' } }}
              >
                Registrieren
              </Button>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PublicNavBar;
